import {
  CompanyReportDetail,
  CompanyReportPreviewResults,
  CompanyReportsListParams,
  PaginatedCompanyReportList,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';
import { useAxiosConfig } from '../../AxiosConfigProvider';
import {
  getCompanyReportsRetrieveQueryKey,
  getCompanyReportsValidReportingYearsRetrieveQueryKey,
  useCompanyReportsCategoryStatusUpdate,
  useCompanyReportsCreate,
  useCompanyReportsList,
  useCompanyReportsPreviewRetrieve,
  useCompanyReportsRetrieve,
} from '../../lib/company-reports/company-reports';

export const useCreateCompanyReport = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          'company-reports',
          getCompanyReportsValidReportingYearsRetrieveQueryKey(),
        ]);
      },
    },
  });
};

export const useGetCompanyReportsList = (
  params?: CompanyReportsListParams,
  options?: {
    polling?: boolean;
    pollingInterval?: number;
  }
) => {
  const { polling, pollingInterval } = options || {};

  return useCompanyReportsList<PaginatedCompanyReportList>(params, {
    request: useAxiosConfig(),
    query: {
      refetchInterval: polling && (pollingInterval || 1000),
      refetchIntervalInBackground: polling,
    },
  });
};

export const useGetCompanyReport = (
  companyReportUuid: string,
  options?: {
    polling?: boolean;
    pollingInterval?: number;
    enabled?: boolean;
  }
) => {
  return useCompanyReportsRetrieve<CompanyReportDetail>(companyReportUuid, {
    request: useAxiosConfig(),
    query: {
      enabled: !!companyReportUuid && (options?.enabled ?? true),
      refetchInterval: options?.polling && (options?.pollingInterval || 1000),
      refetchIntervalInBackground: options?.polling,
    },
  });
};

export const useGetCompanyReportPreview = ({
  companyReportUuid,
  enabled = true,
}: {
  companyReportUuid: string;
  enabled?: boolean;
}) => {
  return useCompanyReportsPreviewRetrieve<CompanyReportPreviewResults>(
    companyReportUuid,
    {
      request: useAxiosConfig(),
      query: {
        enabled,
      },
    }
  );
};

export const useUpdateCategoryStatus = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsCategoryStatusUpdate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_data, variables) => {
        await queryClient.invalidateQueries(
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
      },
    },
  });
};
